/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { css } from "@emotion/css";
import { Button, Tooltip } from "@octopusdeploy/design-system-components";
import { UnavailableIcon } from "@octopusdeploy/design-system-icons";
import type { DashboardItemResource, DashboardProjectResource, ReferenceDataItem, DashboardTenantResource, EnvironmentLiveStatusResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import * as React from "react";
import type { ActionEvent, AnalyticActionDispatcher } from "~/analytics/Analytics";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { DeploymentTaskStatus } from "~/areas/dashboard/Deployments/DeploymentTaskStatus";
import PermissionCheck from "~/components/PermissionCheck/PermissionCheck";
import type { DeploymentContext } from "../ProjectDashboard";
import styles from "./style.module.less";
interface DashboardCellProps {
    spaceId: string;
    deployment: DashboardItemResource | null;
    deployments: DashboardItemResource[];
    deploymentContext: DeploymentContext;
    hasReleases: boolean;
    tenants: DashboardTenantResource[];
    environment: ReferenceDataItem;
    project: DashboardProjectResource;
    nextAvailableDeploymentEnvironments: {
        [environmentId: string]: string[];
    } | undefined;
    allowDeployments: boolean;
    channelName: string;
    releaseVersion: string;
    showChannelChips: boolean;
    projectEnvironmentTenantIds: string[];
    liveStatus: EnvironmentLiveStatusResource | undefined;
}
interface DashboardCellPropsInternal extends DashboardCellProps {
    dispatchAction: AnalyticActionDispatcher;
}
class DashboardCellInternal extends React.PureComponent<DashboardCellPropsInternal> {
    render() {
        const { deployments, deploymentContext, allowDeployments } = this.props;
        if (deployments && deployments.length) {
            return this.renderTaskStatusCell();
        }
        if (!allowDeployments) {
            return null;
        }
        const { projectId, releaseId, environmentId, tenantId } = deploymentContext;
        // If no tenant or tenant is enabled, we will not disable the cell
        let disabledCell = false;
        // You've got a tenant... but this tenant can't deploy this environment so... nah
        if (!!tenantId) {
            const tenant = this.props.tenants.find((t) => t.Id === tenantId);
            disabledCell = !!tenant && tenant.IsDisabled;
            if (tenant && tenant.ProjectEnvironments[projectId].indexOf(environmentId) === -1) {
                const environment = this.props.environment;
                return <div className={cn(styles.cell, { [cellDisabledStyles]: disabledCell })}>{this.renderNoDeployCell(`${tenant.Name} is not linked to the ${environment.Name} environment`, projectId, environmentId, tenantId)}</div>;
            }
        }
        // We are looking at an untenanted deployment on a project that can't do untenanted deployments, so... nope.
        if (!tenantId && !this.props.project.CanPerformUntenantedDeployment) {
            return <div className={cn(styles.cell, { [cellDisabledStyles]: disabledCell })}>{this.renderNoDeployCell(`This project can not perform untenanted deployments`, projectId, environmentId)}</div>;
        }
        // No release
        if (!releaseId) {
            let text = "Create a release first to deploy project";
            if (this.props.hasReleases) {
                // has releases
                text = "Filter the dashboard by releases to perform a deployment";
            }
            return (<div className={cn(styles.cell, { [cellDisabledStyles]: true })}>
                    <Tooltip content={text}>Filter required</Tooltip>
                </div>);
        }
        // This scenario is likely only available when we add more filters
        if (!environmentId || !projectId) {
            return <div className={cn(styles.cell, { [cellDisabledStyles]: disabledCell })}>{this.getUnavailableToDeployWarning(projectId, environmentId)}</div>;
        }
        // Ok... so looks like you can deploy to this environment
        const { nextAvailableDeploymentEnvironments } = this.props;
        if (nextAvailableDeploymentEnvironments) {
            const tenantReleases = nextAvailableDeploymentEnvironments[environmentId];
            if (tenantReleases && tenantReleases.indexOf(tenantId) !== -1) {
                // don't worry. when untenanted tenantId=null
                const uri = links.deployReleasePage.generateUrl({ spaceId: this.props.spaceId, projectSlug: this.props.project.Slug, releaseVersion: this.props.releaseVersion }, { environmentIds: [environmentId], tenantIds: tenantId ? [tenantId] : undefined });
                return (<div className={cn(styles.cell, { [cellDisabledStyles]: disabledCell })}>
                        <PermissionCheck permission={Permission.DeploymentCreate} project={projectId} environment={environmentId} tenant={tenantId}>
                            <Tooltip content={"Prepare and preview a deployment to this environment"}>
                                <Button label="Deploy..." title="" href={uri} importance="secondary" onClick={() => {
                        const ev: ActionEvent = {
                            action: Action.Deploy,
                            resource: "Deploy Release",
                        };
                        this.props.dispatchAction("Initiate Deployment", ev);
                    }} disabled={disabledCell}/>
                            </Tooltip>
                        </PermissionCheck>
                    </div>);
            }
        }
        return (<div className={cn(styles.cell, { [cellDisabledStyles]: disabledCell })}>
                {this.renderNoDeployCell(`Release ${this.props.releaseVersion} can not be deployed to the ${this.props.environment.Name} environment`, projectId, environmentId, tenantId)}
            </div>);
    }
    private renderTaskStatusCell() {
        return (<div className={cn(styles.taskDetailsCell)}>
                <DeploymentTaskStatus spaceId={this.props.spaceId} projectSlug={this.props.project.Slug} deployment={this.props.deployment!} isCollapsed={false} liveStatus={this.props.liveStatus} channelName={this.props.showChannelChips ? this.props.channelName : undefined} 
        // We never show tenant progress on the project dashboard
        tenantProgress={"hidden"}/>
            </div>);
    }
    private renderNoDeployCell(message: string, projectId: string, environmentId: string, tenantId?: string) {
        const tenant = this.props.tenants.find((t) => t.Id === tenantId);
        const disableCell = !!tenant && tenant.IsDisabled;
        return (<PermissionCheck permission={Permission.DeploymentCreate} project={projectId} environment={environmentId} tenant={tenantId}>
                <Tooltip content={message}>
                    <div className={cn(styles.blockIcon, { [cellDisabledStyles]: disableCell })}>
                        <UnavailableIcon size={24}/>
                    </div>
                </Tooltip>
            </PermissionCheck>);
    }
    private getUnavailableToDeployWarning = (projectId: string, environmentId: string) => {
        const missing: string[] = [];
        if (!projectId) {
            missing.push("project");
        }
        if (!environmentId) {
            missing.push("environment");
        }
        let missingDescription: string = "";
        if (missing.length === 1) {
            missingDescription = missing[0];
        }
        else if (missing.length > 1) {
            missingDescription = missing
                .slice(0, missing.length - 1)
                .join(", ")
                .concat(" and ")
                .concat(missing[missing.length - 1]);
        }
        const text = `Filter the dashboard by ${missingDescription} to perform a deployment.`;
        return (<Tooltip content={text}>
                <Button label="Filter required" disabled={true} importance="quiet" href={"#"}/>
            </Tooltip>);
    };
    static displayName = "DashboardCellInternal";
}
const cellDisabledStyles = css({
    opacity: 0.4,
});
export function DashboardCell(props: DashboardCellProps) {
    const dispatchAction = useProjectScopedAnalyticActionDispatch(props.project.Id);
    return <DashboardCellInternal {...props} dispatchAction={dispatchAction}/>;
}
